import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft, FloppyDisk, PaperPlaneTilt, FilePdf, Trash, ChatCircleDots, Lock, Flask } from "@phosphor-icons/react";
import CommentsDrawer from "@/components/CommentsDrawer";
import ResearchDrawer from "@/components/ResearchDrawer";
import { useAuth } from "@/context/AuthContext";

function mdToHtml(md) {
  if (!md) return "";
  const lines = md.split("\n");
  const out = [];
  let inList = false;
  for (const raw of lines) {
    const line = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");
    if (/^#\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h1>${line.slice(2)}</h1>`); }
    else if (/^##\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h2>${line.slice(3)}</h2>`); }
    else if (/^###\s/.test(line)) { if (inList) { out.push("</ul>"); inList = false; } out.push(`<h3>${line.slice(4)}</h3>`); }
    else if (/^\s*[-*]\s/.test(line)) {
      if (!inList) { out.push("<ul>"); inList = true; }
      out.push(`<li>${line.replace(/^\s*[-*]\s/, "")}</li>`);
    } else if (line.trim() === "") {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push("");
    } else {
      if (inList) { out.push("</ul>"); inList = false; }
      out.push(`<p>${line}</p>`);
    }
  }
  if (inList) out.push("</ul>");
  return out.join("\n");
}

// Match [PLACEHOLDER] tokens the AI uses for fields the applicant must fill in
// — [XX%], [$XXX,XXX], [Name/Title], [describe relevant infrastructure...].
// Negative lookahead `(?!\()` skips markdown-link syntax `[text](url)`.
const PLACEHOLDER_RE = /\[([^[\]\n]{1,200})\](?!\()/g;

function countPlaceholders(text) {
  if (!text) return 0;
  const matches = text.match(PLACEHOLDER_RE);
  return matches ? matches.length : 0;
}

// Normalize a [PLACEHOLDER] token so visually-similar tokens collapse to one
// key. Strips brackets, lowercases, removes punctuation/spaces. So [Name/Title],
// [name, title], [NAME TITLE] all map to "nametitle".
function normalizePlaceholder(token) {
  return token.replace(/^\[|\]$/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function highlightPlaceholders(html) {
  // Wrap each [PLACEHOLDER] in <mark class="gf-placeholder" title="…">.
  return html.replace(
    PLACEHOLDER_RE,
    (m) => `<mark class="gf-placeholder" title="Replace this placeholder with your details">${m}</mark>`,
  );
}

// HighlightedMarkdownArea: a plain <textarea> overlaid on a <div> backdrop
// that renders the same text with yellow <mark> highlights on [PLACEHOLDER]
// tokens. Both layers share identical typography & padding, so the marks
// align pixel-perfect under the editable text. Scroll positions are synced.
function HighlightedMarkdownArea({ value, onChange, disabled, textareaRef }) {
  const backdropRef = useRef(null);

  // Escape HTML, then wrap placeholders. We deliberately keep a trailing
  // newline as a non-breaking newline so the backdrop's height matches the
  // textarea (otherwise the final blank line is stripped).
  const html = useMemo(() => {
    const safe = (value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    const withMarks = safe.replace(
      PLACEHOLDER_RE,
      (m) => `<mark class="gf-placeholder">${m}</mark>`,
    );
    return withMarks + "\n";
  }, [value]);

  const onScroll = (e) => {
    const bd = backdropRef.current;
    if (!bd) return;
    bd.scrollTop = e.target.scrollTop;
    bd.scrollLeft = e.target.scrollLeft;
  };

  return (
    <div className="gf-htxt-wrap gf-sharp">
      <div
        ref={backdropRef}
        className="gf-htxt-backdrop"
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, {
          ALLOWED_TAGS: ["mark", "br"],
          ALLOWED_ATTR: ["class"],
        }) }}
      />
      <textarea
        ref={textareaRef}
        data-testid="proposal-content-input"
        className="gf-htxt-area"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onScroll={onScroll}
        disabled={disabled}
        spellCheck={false}
      />
    </div>
  );
}

// Render proposal HTML with each [PLACEHOLDER] swapped for an inline <input>.
// Inputs are UNCONTROLLED to avoid focus loss on every keystroke; we sync to
// the markdown source only on blur via a delegated handler on the wrapper.
function inputifyPlaceholders(html) {
  let idx = 0;
  return html.replace(PLACEHOLDER_RE, (m) => {
    const safe = m.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const size = Math.max(8, Math.min(60, m.length + 2));
    const i = idx;
    idx += 1;
    return `<input class="gf-ph-input" data-ph-idx="${i}" value="${safe}" size="${size}" aria-label="Edit placeholder" />`;
  });
}

function FillInView({ content, setContent, readOnly }) {
  const containerRef = useRef(null);

  const safeHtml = useMemo(() => {
    const html = inputifyPlaceholders(mdToHtml(content));
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "strong", "em", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code", "pre", "mark", "input"],
      ALLOWED_ATTR: ["class", "title", "data-ph-idx", "value", "size", "aria-label"],
    });
    // Re-render only when the SOURCE content changes — typing into an input
    // does not trigger a re-render (uncontrolled), so focus is preserved.
  }, [content]);

  // Delegated blur — replaces the Nth placeholder in `content` with whatever
  // the user typed. React's synthetic onBlur bubbles, so one listener is enough.
  // Also offers a one-click "apply to all similar placeholders" suggestion when
  // the user just filled in a token shape that appears elsewhere unfilled.
  const handleBlur = (e) => {
    const tgt = e.target;
    if (!tgt || !tgt.classList || !tgt.classList.contains("gf-ph-input")) return;
    const idxAttr = tgt.getAttribute("data-ph-idx");
    if (idxAttr === null) return;
    const idx = parseInt(idxAttr, 10);
    const next = tgt.value;

    // Find the original token at index `idx` BEFORE we mutate content.
    let originalAtIdx = null;
    let scanN = 0;
    content.replace(PLACEHOLDER_RE, (m) => {
      if (scanN === idx) originalAtIdx = m;
      scanN += 1;
      return m;
    });
    const valueChanged = originalAtIdx !== null && next !== originalAtIdx;

    // Splice the typed value into the Nth placeholder slot.
    let n = 0;
    const updated = content.replace(PLACEHOLDER_RE, (m) => {
      const replacement = n === idx ? next : m;
      n += 1;
      return replacement;
    });
    if (updated === content) return;
    setContent(updated);

    // Smart defaults: when a placeholder is actually filled in (i.e. the new
    // value no longer matches the [bracket] pattern), offer to apply the same
    // value to all other unfilled placeholders with the same normalized shape.
    if (!valueChanged) return;
    const nextLooksFilled = !PLACEHOLDER_RE.test(next);
    PLACEHOLDER_RE.lastIndex = 0;
    if (!nextLooksFilled) return;
    const shape = normalizePlaceholder(originalAtIdx);
    if (!shape) return;
    // Count siblings — other UNFILLED tokens with the same normalized shape.
    const siblings = [];
    let si = 0;
    updated.replace(PLACEHOLDER_RE, (m) => {
      if (si !== idx && normalizePlaceholder(m) === shape) siblings.push(si);
      si += 1;
      return m;
    });
    if (siblings.length === 0) return;
    toast.message(
      `Apply "${next}" to ${siblings.length} similar field${siblings.length === 1 ? "" : "s"}?`,
      {
        description: `Same shape as ${originalAtIdx}`,
        duration: 8000,
        action: {
          label: "Apply all",
          onClick: () => {
            let aj = 0;
            const bulk = updated.replace(PLACEHOLDER_RE, (m) => {
              const isSibling = aj !== idx && normalizePlaceholder(m) === shape;
              aj += 1;
              return isSibling ? next : m;
            });
            setContent(bulk);
            toast.success(`Applied to ${siblings.length} field${siblings.length === 1 ? "" : "s"}.`);
          },
        },
      },
    );
  };

  // Tag any input whose current value no longer matches the [bracket] pattern
  // with a "filled" class — gives the user a visual confirmation. Runs after
  // every render of the HTML.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    root.querySelectorAll("input.gf-ph-input").forEach((el) => {
      const filled = !PLACEHOLDER_RE.test(el.value);
      // PLACEHOLDER_RE has /g flag; reset state to avoid leaking lastIndex.
      PLACEHOLDER_RE.lastIndex = 0;
      el.classList.toggle("gf-ph-filled", filled);
      el.disabled = readOnly;
    });
  }, [safeHtml, readOnly]);

  return (
    <div
      ref={containerRef}
      data-testid="proposal-fillin"
      onBlur={handleBlur}
      onContextMenu={(e) => e.preventDefault()}
      className="border border-zinc-200 bg-white p-8 md:p-12 prose-grant"
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
}

export default function ProposalEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // "Fill-in" mode is premium-only. Trial users see a paywall card on the tab.
  const isPaidSubscriber = user?.subscription_status === "premium" || user?.subscription_status === "lifetime";
  const [p, setP] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [researchOpen, setResearchOpen] = useState(false);
  const lastSaveRef = useRef(0);
  const contentRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/proposals/${id}`);
    setP(data);
    setTitle(data.title);
    setContent(data.content);
  };

  useEffect(() => { load().catch(() => navigate("/proposals")); }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/proposals/${id}`, { title, content });
      setP(data);
      lastSaveRef.current = Date.now();
      toast.success("Proposal saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const openSubmit = () => {
    // Auto-fill recipient from the grant's agency contact if we captured it.
    setRecipient(p?.agency_contact_email || "");
    setSubmitOpen(true);
  };

  const submit = async () => {
    if (!recipient.trim()) {
      toast.error("Recipient email is required.");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/proposals/${id}`, { title, content });
      const { data } = await api.post(`/proposals/${id}/submit`, {
        method: "email",
        recipient_email: recipient.trim(),
      });
      setP(data);
      setSubmitOpen(false);
      toast.success(`Submitted to ${data.submission_email} — Ref ${data.submission_ref}`);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this proposal?")) return;
    await api.delete(`/proposals/${id}`);
    navigate("/proposals");
  };

  if (!p) return <div className="gf-overline">Loading…</div>;

  const canEdit = p.can_edit !== false; // backend returns false when read-only
  const isCompanyView = !!p.is_company_view;
  const isSubmitted = p.status === "submitted";
  const isReadOnly = !canEdit || isSubmitted;
  const pdfUrl = `${API}/proposals/${id}/pdf`;
  const safeHtml = DOMPurify.sanitize(highlightPlaceholders(mdToHtml(content)), {
    ALLOWED_TAGS: ["p", "br", "ul", "ol", "li", "strong", "em", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "code", "pre", "mark"],
    ALLOWED_ATTR: ["class", "title"],
  });
  const placeholdersLeft = countPlaceholders(content);

  // Jump caret + selection to the next [PLACEHOLDER] in the textarea, so
  // editing each open field is a single click → start typing flow.
  const findNextPlaceholder = () => {
    const ta = contentRef.current;
    if (!ta) return;
    const from = ta.selectionEnd || 0;
    const re = new RegExp(PLACEHOLDER_RE.source, "g");
    re.lastIndex = from;
    let m = re.exec(content);
    if (!m) {
      // Wrap around from the top.
      re.lastIndex = 0;
      m = re.exec(content);
    }
    if (!m) {
      toast.success("All placeholders are filled in. Looking good.");
      return;
    }
    ta.focus();
    ta.setSelectionRange(m.index, m.index + m[0].length);
    // Scroll the textarea so the selection is visible.
    const before = content.slice(0, m.index);
    const linesBefore = before.split("\n").length;
    const approxLineHeight = 22;
    ta.scrollTop = Math.max(0, linesBefore * approxLineHeight - 120);
  };

  // Same as above, but searches BACKWARDS from the current caret. Wraps to
  // the bottom of the document when no earlier placeholder exists.
  const findPrevPlaceholder = () => {
    const ta = contentRef.current;
    if (!ta) return;
    const before = ta.selectionStart || 0;
    const re = new RegExp(PLACEHOLDER_RE.source, "g");
    // Collect every match, then pick the last one that ends BEFORE the caret.
    const matches = [];
    let m;
    while ((m = re.exec(content)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length });
    }
    if (matches.length === 0) {
      toast.success("All placeholders are filled in. Looking good.");
      return;
    }
    const prev = [...matches].reverse().find((x) => x.end <= before) || matches[matches.length - 1];
    ta.focus();
    ta.setSelectionRange(prev.start, prev.end);
    const beforeText = content.slice(0, prev.start);
    const linesBefore = beforeText.split("\n").length;
    const approxLineHeight = 22;
    ta.scrollTop = Math.max(0, linesBefore * approxLineHeight - 120);
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-900 uppercase tracking-wide" data-testid="editor-back">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" data-testid="pdf-btn" className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              <FilePdf size={16} className="mr-2" /> PDF
            </a>
          </Button>
          <Button onClick={() => setResearchOpen(true)} variant="outline" data-testid="research-btn" className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">
            <Flask size={16} className="mr-2" /> Research
          </Button>
          <Button onClick={() => setCommentsOpen(true)} variant="outline" data-testid="comments-btn" className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">
            <ChatCircleDots size={16} className="mr-2" /> Review
          </Button>
          <Button onClick={save} disabled={saving || isReadOnly} variant="outline" data-testid="save-btn" className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">
            <FloppyDisk size={16} className="mr-2" /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button onClick={openSubmit} disabled={isReadOnly || placeholdersLeft > 0} data-testid="submit-btn" title={placeholdersLeft > 0 ? `Fill in all ${placeholdersLeft} placeholder${placeholdersLeft === 1 ? "" : "s"} before submitting` : undefined} className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide disabled:opacity-50">
            <PaperPlaneTilt size={16} className="mr-2" /> {isSubmitted ? "Submitted" : "Submit"}
          </Button>
          <Button onClick={del} disabled={!canEdit} variant="outline" data-testid="delete-btn" className="gf-sharp border-zinc-300 text-red-700 uppercase font-bold tracking-wide">
            <Trash size={16} />
          </Button>
        </div>
      </div>

      {isCompanyView && (
        <div data-testid="company-view-banner" className="mt-4 border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 text-sm flex items-start gap-3">
          <span className="gf-overline shrink-0 mt-0.5">Company view</span>
          <span>
            This proposal belongs to <b>{p.owner_name || p.owner_email}</b>
            {!canEdit && " — you have read-only access. Ask a manager to edit on your behalf."}
            {canEdit && " — as a manager you can edit, submit, or delete it."}
          </span>
        </div>
      )}

      <div className="mt-6 border border-zinc-200 bg-white p-6" data-testid="proposal-meta">
        <div className="gf-overline">Grant Opportunity</div>
        <Link to={`/grants/${p.grant_id}`} className="block font-semibold text-zinc-900 mt-1 hover:text-blue-700 truncate">
          {p.grant_title}
        </Link>
        <div className="flex flex-wrap gap-4 mt-2 text-xs font-mono text-zinc-500">
          <span>#{p.grant_number}</span>
          <span>{p.grant_agency}</span>
          <span>Status: <b className={`uppercase ${
            p.status === "submitted" ? "text-emerald-700"
            : (p.status === "draft" && placeholdersLeft === 0) ? "text-blue-700"
            : "text-amber-700"
          }`} data-testid="proposal-status-badge">{
            p.status === "draft" && placeholdersLeft === 0 ? "ready" : p.status
          }</b></span>
          {p.submission_ref && <span>Ref: {p.submission_ref}</span>}
          {p.submission_method && <span>Method: {p.submission_method}</span>}
          {p.submission_email && <span>Sent to: {p.submission_email}</span>}
        </div>
      </div>

      <div className="mt-6">
        <Label className="gf-overline">Proposal Title</Label>
        <Input
          data-testid="proposal-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={isReadOnly}
          className="gf-sharp mt-2 border-zinc-300 bg-white text-lg font-semibold"
        />
      </div>

      <Tabs defaultValue="fillin" className="mt-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <TabsList className="gf-sharp bg-white border border-zinc-200 p-0 h-auto">
            <TabsTrigger value="fillin" data-testid="tab-fillin" className="gf-sharp rounded-none px-6 py-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white uppercase tracking-wider font-bold text-xs">
              Fill in
            </TabsTrigger>
            <TabsTrigger value="edit" data-testid="tab-edit" className="gf-sharp rounded-none px-6 py-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white uppercase tracking-wider font-bold text-xs">
              Edit
            </TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview" className="gf-sharp rounded-none px-6 py-2 data-[state=active]:bg-blue-700 data-[state=active]:text-white uppercase tracking-wider font-bold text-xs">
              Preview
            </TabsTrigger>
          </TabsList>
          {placeholdersLeft > 0 ? (
            <div
              data-testid="placeholder-counter"
              className="flex items-center gap-2 border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs"
            >
              <span className="font-mono font-bold text-amber-900 uppercase tracking-wide">
                {placeholdersLeft} placeholder{placeholdersLeft === 1 ? "" : "s"} to fill
              </span>
              <Button
                type="button"
                onClick={findPrevPlaceholder}
                disabled={isReadOnly}
                data-testid="find-prev-placeholder"
                size="sm"
                variant="outline"
                className="gf-sharp border-amber-400 bg-white text-amber-900 hover:bg-amber-100 font-bold uppercase tracking-wide text-[10px] px-2 py-1 h-auto"
              >
                ← Prev
              </Button>
              <Button
                type="button"
                onClick={findNextPlaceholder}
                disabled={isReadOnly}
                data-testid="find-next-placeholder"
                size="sm"
                className="gf-sharp bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide text-[10px] px-2 py-1 h-auto"
              >
                Next →
              </Button>
            </div>
          ) : content && (
            <div
              data-testid="placeholder-counter-done"
              className="border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-mono font-bold text-emerald-900 uppercase tracking-wide"
            >
              ✓ All placeholders filled
            </div>
          )}
        </div>
        <TabsContent value="fillin" className="mt-3">
          {isPaidSubscriber ? (
            <FillInView content={content} setContent={setContent} readOnly={isReadOnly} />
          ) : (
            <div data-testid="fillin-paywall" className="border border-amber-300 bg-amber-50 p-8 md:p-12 text-center">
              <Lock size={32} className="mx-auto text-amber-700" />
              <h3 className="mt-4 font-display font-extrabold text-2xl tracking-tight text-zinc-900">
                Click-to-fill editing is a paid feature
              </h3>
              <p className="mt-3 text-sm text-zinc-700 leading-relaxed max-w-md mx-auto">
                Upgrade to a paid plan to fill in every <code className="bg-amber-100 px-1.5 py-0.5 text-amber-900 font-mono text-xs">[bracketed placeholder]</code> directly inside the rendered proposal — no scrolling through markdown, no missed fields.
              </p>
              <Button asChild data-testid="fillin-upgrade-cta" className="gf-sharp mt-6 bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase tracking-wide">
                <Link to="/subscribe">Upgrade to unlock</Link>
              </Button>
              <p className="mt-4 text-xs text-zinc-500 font-mono uppercase tracking-wide">
                Trial users can still use Edit + Preview tabs.
              </p>
            </div>
          )}
        </TabsContent>
        <TabsContent value="edit" className="mt-3">
          <HighlightedMarkdownArea
            value={content}
            onChange={setContent}
            disabled={isReadOnly}
            textareaRef={contentRef}
          />
        </TabsContent>
        <TabsContent value="preview" className="mt-3">
          <div
            data-testid="proposal-preview"
            onContextMenu={(e) => e.preventDefault()}
            onCopy={(e) => {
              const sel = (window.getSelection()?.toString() || "").trim();
              if (sel.length > 200) {
                e.preventDefault();
                toast.message("Bulk copy disabled — open the PDF for export.");
              }
            }}
            className="border border-zinc-200 bg-white p-8 md:p-12 prose-grant select-text"
            style={{ position: "relative" }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="gf-sharp border-zinc-200" data-testid="submit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">Submit proposal</DialogTitle>
            <DialogDescription>
              Email the proposal PDF directly to the agency contact. The PDF is signed with a tracking reference.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border border-zinc-200 bg-zinc-50 p-3 text-xs">
              <div className="gf-overline">Grant</div>
              <div className="font-semibold mt-1">{p.grant_title}</div>
              <div className="text-zinc-600">{p.grant_agency} · #{p.grant_number}</div>
            </div>
            <div>
              <Label className="gf-overline">Recipient email</Label>
              <Input
                data-testid="recipient-email"
                type="email"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                placeholder="grants.officer@agency.gov"
                className="gf-sharp mt-2 border-zinc-300 bg-white"
                autoFocus
              />
              {p.agency_contact_email && recipient === p.agency_contact_email && (
                <p className="text-[11px] text-emerald-700 mt-2 font-mono">
                  ✓ Auto-filled from Grants.gov agency contact{p.agency_contact_name ? ` (${p.agency_contact_name})` : ""}.
                </p>
              )}
              {!p.agency_contact_email && (
                <p className="text-[11px] text-amber-700 mt-2 font-mono">
                  No agency contact on file for this grant — please enter the grants officer email manually.
                </p>
              )}
              <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                The proposal PDF will be sent from <code className="bg-zinc-100 px-1 py-0.5">support@grantflow.it.com</code>.
                Reply-to will be set to your account email.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitOpen(false)} className="gf-sharp border-zinc-300 uppercase font-bold tracking-wide">Cancel</Button>
            <Button onClick={submit} disabled={submitting || !recipient.trim()} data-testid="confirm-submit" className="gf-sharp bg-blue-700 hover:bg-blue-800 text-white font-bold uppercase tracking-wide">
              {submitting ? "Sending…" : "Send proposal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CommentsDrawer
        open={commentsOpen}
        onOpenChange={setCommentsOpen}
        proposalId={id}
        proposalOwnerId={p.user_id}
      />
      <ResearchDrawer
        open={researchOpen}
        onOpenChange={setResearchOpen}
        defaultKeyword={p.keyword || p.grant_title}
      />
    </div>
  );
}
